import React, { CSSProperties } from "react";
import { Shell } from "@/shell/Shell";
import { PageHeader } from "@/shell/PageHeader";
import { FiltersBar } from "@/features/orders-analytics/components/FiltersBar";
import { useAvailableClients } from "@/features/orders-analytics/hooks/use-available-clients";
import { useOrdersAnalyticsFilters } from "@/features/orders-analytics/hooks/use-orders-analytics-filters";
import { useMetaGrowthData } from "@/features/meta-growth/hooks/use-meta-growth-data";
import { metaGrowthViewModel } from "@/features/meta-growth/pages/meta-growth-view-model";
import { SectionHeader } from "@/shell/components/SectionHeader";
import { StatusBanner } from "@/shell/components/StatusBanner";
import { KpiCard } from "@/shell/components/KpiCard";

const EMPTY_STATE_TEXT = "Sin datos disponibles — no hay fuente real para esta sección.";

export function MetaGrowthPage() {
    const { filters, setFilters } = useOrdersAnalyticsFilters();
    const { availableClients, loading, refresh } = useAvailableClients(filters);
    const {
        data,
        loading: growthLoading,
        error: growthError,
        refresh: refreshGrowth,
    } = useMetaGrowthData(filters);

    const vm = metaGrowthViewModel.fromData(data);
    const showError = growthError !== null;
    const showLoading = growthLoading && data === null && !showError;
    const showSections = !showError && !showLoading;

    const handleRefresh = () => {
        refresh();
        refreshGrowth();
    };

    return (
        <Shell title="Meta Growth" subtitle="Blended attribution & advisories">
            <PageHeader
                title="Meta Growth"
                lede="Blended, cohort-level attribution: ad spend & results against audience worth. Scoped to enviaDinero — this dashboard is single-tenant. No per-order campaign attribution: value and spend are joined only on client + time window."
                right={
                    <FiltersBar
                        filters={filters}
                        availableClients={availableClients}
                        onFiltersChange={setFilters}
                        onRefresh={handleRefresh}
                        loading={loading || growthLoading}
                        // This dashboard is single-tenant by design: the backend forces
                        // the tenant in both read use-cases and cannot honour a client
                        // choice, so offering one would relabel enviaDinero's revenue
                        // with another client's name. The scope is stated in the lede.
                        showClientSelector={false}
                    />
                }
            />

            {showError ? (
                <StatusBanner
                    severity="critical"
                    title="No se pudo cargar el panel de Meta Growth"
                    sub="El backend de Metrics no pudo leer los datos de Meta Growth. Reintenta o avisa a ingeniería."
                    action={
                        <button type="button" className="btn btn--primary btn--sm" onClick={refreshGrowth}>
                            Reintentar
                        </button>
                    }
                />
            ) : null}

            {showLoading ? (
                <div className="td-muted" style={loadingStyle}>
                    Cargando datos de Meta Growth…
                </div>
            ) : null}

            {showSections ? (
                <>
                    <section className="section">
                        <SectionHeader
                            title="Blended attribution"
                            meta="windowed revenue ÷ windowed ad spend · null when unavailable"
                        />
                        <div className="grid-4">
                            <KpiCard
                                icon="trending_up"
                                label="Blended ROAS"
                                value={vm.blendedRoas}
                                sub="windowed revenue per unit of ad spend"
                                mom={null}
                                tint="primary"
                            />
                            <KpiCard
                                icon="payments"
                                label="Windowed revenue"
                                value={vm.windowedValue}
                                sub="completed operations-read revenue in window"
                                mom={null}
                                tint="primary"
                            />
                            <KpiCard
                                icon="ad_units"
                                label="Ad spend"
                                value={vm.panelB.totalSpend}
                                sub="Meta ad spend in window"
                                mom={null}
                                tint="primary"
                            />
                        </div>
                    </section>

                    <section className="section">
                        <SectionHeader
                            title="Audience worth"
                            count={vm.panelA.rows.length}
                            meta={vm.panelA.basis}
                        />
                        {vm.panelA.truncated ? (
                            <div className="td-muted" style={truncatedNoticeStyle}>
                                Los datos podrían estar incompletos: una o más audiencias superan el límite de lectura y su valor es parcial.
                            </div>
                        ) : null}
                        {vm.panelA.degraded.length > 0 ? (
                            <div className="td-muted" style={truncatedNoticeStyle}>
                                Total incompleto: no se pudo leer {vm.panelA.degraded.length}{" "}
                                {vm.panelA.degraded.length === 1 ? "audiencia" : "audiencias"} ({vm.panelA.degraded.join(", ")}). Reintenta para obtener el total completo.
                            </div>
                        ) : null}
                        {vm.panelA.rows.length === 0 ? (
                            <div className="td-muted" style={emptySectionStyle}>
                                {EMPTY_STATE_TEXT}
                            </div>
                        ) : (
                            <div className="tbl-wrap">
                                <table className="tbl">
                                    <thead>
                                        <tr>
                                            <th>Audience</th>
                                            <th className="td-num">Members</th>
                                            <th className="td-num">Worth (all-time)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {vm.panelA.rows.map((row) => (
                                            <tr key={row.audienceSpecId}>
                                                <td className="td-strong">{row.label}</td>
                                                <td className="td-num">{row.memberCount}</td>
                                                <td className="td-num td-strong">{row.value}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td className="td-strong">Total</td>
                                            <td className="td-num" />
                                            <td className="td-num td-strong">{vm.panelA.totalValue}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}
                    </section>

                    <section className="section">
                        <SectionHeader
                            title="Campaign spend & results"
                            count={vm.panelB.rows.length}
                            meta={vm.panelB.basis}
                        />
                        {vm.panelB.rows.length === 0 ? (
                            <div className="td-muted" style={emptySectionStyle}>
                                {EMPTY_STATE_TEXT}
                            </div>
                        ) : (
                            <div className="tbl-wrap">
                                <table className="tbl">
                                    <thead>
                                        <tr>
                                            <th>Campaign</th>
                                            <th className="td-num">Spend</th>
                                            <th className="td-num">Results</th>
                                            <th className="td-num">Cost per result</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {vm.panelB.rows.map((row) => (
                                            <tr key={row.objectId}>
                                                <td className="td-strong">{row.objectName}</td>
                                                <td className="td-num td-strong">{row.spend}</td>
                                                <td className="td-num">{row.results}</td>
                                                <td className="td-num">{row.costPerResult}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>

                    <section className="section">
                        <SectionHeader title="Recommendations" count={vm.recommendations.length} meta="advisory signals" />
                        {vm.recommendations.length === 0 ? (
                            <div className="td-muted" style={emptySectionStyle}>
                                {EMPTY_STATE_TEXT}
                            </div>
                        ) : (
                            <div style={recommendationsStyle}>
                                {vm.recommendations.map((recommendation, index) => (
                                    <div key={`${recommendation.code}-${index}`} className="card" style={recommendationCardStyle}>
                                        <span
                                            className="material-symbols-outlined"
                                            style={recommendation.severity === "warning" ? warningIconStyle : infoIconStyle}
                                        >
                                            {recommendation.severity === "warning" ? "warning" : "info"}
                                        </span>
                                        <span>{recommendation.message}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </>
            ) : null}
        </Shell>
    );
}

const emptySectionStyle: CSSProperties = { padding: "16px 4px", fontSize: 13 };

const truncatedNoticeStyle: CSSProperties = { padding: "8px 4px", fontSize: 12 };

const loadingStyle: CSSProperties = { padding: "24px 4px", fontSize: 13 };

const recommendationsStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 8,
};

const recommendationCardStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: 12,
    fontSize: 13,
};

const warningIconStyle: CSSProperties = { color: "var(--vb-warning-500)" };

const infoIconStyle: CSSProperties = { color: "var(--vb-primary-500)" };
